import { Modal, Select } from "antd";
import { useMemo, useState } from "react";
import PrimaryButton from "../Forms/PrimaryButton";
import ConfirmModal from "./ConfirmModal";
import { PiTrash, PiTrashBold } from "react-icons/pi";

const FIXED_FROM_DATE = "2025-01-01";

const daysInMonth = (year, month) => {
  return new Date(year, month, 0).getDate();
};

const subtractMonths = (date, months) => {
  let targetMonth = date.getMonth() + 1 - months;
  let targetYear = date.getFullYear();

  while (targetMonth <= 0) {
    targetMonth += 12;
    targetYear -= 1;
  }

  const lastDayOfTargetMonth = daysInMonth(targetYear, targetMonth);
  const targetDay = Math.min(date.getDate(), lastDayOfTargetMonth);

  return new Date(
    targetYear,
    targetMonth - 1,
    targetDay,
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds(),
  );
};

const formatDateYMD = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getToDateBySelectedValue = (selectedValue) => {
  const today = new Date();
  switch (selectedValue) {
    case "1month":
      return subtractMonths(today, 1);
    case "3months":
      return subtractMonths(today, 3);
    case "6months":
      return subtractMonths(today, 6);
    case "1year":
      return subtractMonths(today, 12);
    default:
      return null;
  }
};

export const PURGE_TIME_OPTIONS = [
  { label: "1 year", value: "1year" },
  { label: "6 months", value: "6months" },
  { label: "3 months", value: "3months" },
  { label: "1 month", value: "1month" },
];

export const usePurgeModal = () => {
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);

  const showModal = () => setOpen(true);
  const hideModal = () => setOpen(false);

  const openConfirm = () => {
    if (!selectedTime) return;
    setConfirmOpen(true);
  };
  const closeConfirm = () => setConfirmOpen(false);

  return {
    open,
    setOpen,
    showModal,
    hideModal,
    confirmOpen,
    setConfirmOpen,
    openConfirm,
    closeConfirm,
    selectedTime,
    setSelectedTime,
  };
};

export const PurgeModalUI = ({
  hookData,
  title = "Purge Notifications",
  width = 400,
  proceedText = "Proceed",
  loading = false,
  onConfirm,
  ...props
}) => {
  const {
    open,
    hideModal,
    confirmOpen,
    openConfirm,
    closeConfirm,
    selectedTime,
    setSelectedTime,
    setOpen,
    setConfirmOpen,
  } = hookData;

  const selectedOption = useMemo(
    () => PURGE_TIME_OPTIONS.find((opt) => opt.value === selectedTime),
    [selectedTime],
  );

  const handleFinalConfirm = async () => {
    if (!selectedTime) return;

    const toDate = getToDateBySelectedValue(selectedTime);
    if (!toDate) return;

    const payload = {
      from_date: FIXED_FROM_DATE,
      to_date: formatDateYMD(toDate),
      // time_type: selectedTime,
    };

    const res = onConfirm ? await onConfirm(payload, selectedTime, selectedOption) : true;
    if (res) {
      setConfirmOpen(false);
      setOpen(false);
    }
  };

  return (
    <>
      <Modal
        title={<p className="font-abhaya text-3xl bg-transparent !m-0">{title}</p>}
        open={open}
        width={width}
        onCancel={hideModal}
        closable={true}
        destroyOnHidden={false}
        zIndex={1000}
        className="form-css"
        footer={
          <div className="flex justify-center mt-6">
            <PrimaryButton
              btnType="primary"
              onClick={openConfirm}
              text={proceedText}
              loading={loading}
              disabled={!selectedTime}
              className="me-[30px]"
            />
          </div>
        }
        {...props}
      >
        <div className="flex flex-col gap-2 mt-4">
          <p className="text-base font-medium text-gray-700">Older than:</p>
          <Select
            value={selectedTime}
            placeholder="Select time"
            className="w-full"
            onChange={(value) => setSelectedTime(value)}
            options={PURGE_TIME_OPTIONS}
            popupClassName="custom-dropdown-menu"
          />
        </div>
      </Modal>

      <ConfirmModal
        actionKey="GENERIC_CONFIRM"
        open={confirmOpen}
        onCancel={closeConfirm}
        onConfirm={handleFinalConfirm}
        loading={loading}
        width={420}
        zIndex={1100}
        title="Confirm Purge"
        description={
          <span className="text-gray-600 block mt-2">
            This is an <span className="font-bold text-[#A33434]">irreversible action</span>. Do you
            want to permanently purge records older than{" "}
            <span className="font-bold text-gray-800">
              {selectedOption?.label || "the selected period"}
            </span>
            ?
          </span>
        }
        confirmText="Confirm"
      />
    </>
  );
};

export const PurgeButton = ({ hookData, buttonLabel = "Purge Notifications", ...props }) => {
  return (
    <PrimaryButton
      text={buttonLabel}
      icon={<PiTrashBold fill="#A33434" />}
      onClick={hookData.showModal}
      iconOnleft
      btnType="ghost"
      iconClassName="!bg-[#a334341a] hover:!bg-[#a334341a]"
      {...props}
    />
  );
};
